import type { TaxEntity } from "../../domain/entities/TaxEntity";
import type { InvoiceOrBillKind } from "./invoiceBillDisplayDiff.types";

/** Loose document shapes — display layer only */
export type InvoiceBillHeaderLike = Record<string, unknown>;
export type InvoiceBillLineLike = Record<string, unknown>;

const trimStr = (v: unknown): string =>
  v === null || v === undefined ? "" : String(v).trim();

/**
 * OCR / classification often stores placeholders instead of a real GSTIN
 * ("Not Available", "N/A", "empty"). Treat those as blank for review diffs so
 * Not Available → "" does not look like a user change.
 */
const BLANKISH_GSTIN_DISPLAY = new Set([
  "",
  "empty",
  "—",
  "-",
  "n/a",
  "na",
  "not available",
  "null",
  "none",
]);

export function normalizeBlankishGstinDisplay(value: unknown): string {
  const trimmed = trimStr(value);
  if (BLANKISH_GSTIN_DISPLAY.has(trimmed.toLowerCase())) return "";
  return trimmed;
}

export function isGstinHeaderFieldKey(key: string): boolean {
  return key === "vendor_gstin" || key === "customer_gstin" || key === "gst_no";
}

/** Match invoice review UI: "March 15, 2024" */
function formatIsoDateHeader(v: unknown): string {
  if (v === null || v === undefined || v === "") return "";
  try {
    const date = new Date(String(v));
    if (Number.isNaN(date.getTime())) return String(v).trim();
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch {
    return String(v).trim();
  }
}

function formatNumberForDisplay(v: unknown): string {
  if (v === null || v === undefined || v === "") return "";
  const n = typeof v === "number" ? v : parseFloat(String(v));
  if (Number.isNaN(n)) return String(v);
  return n.toFixed(2);
}

/** FX rates: preserve precision without forcing 2 decimals */
function formatExchangeRateHeaderDisplay(v: unknown): string {
  if (v === null || v === undefined || v === "") return "";
  const n = typeof v === "number" ? v : parseFloat(String(v));
  if (Number.isNaN(n)) return String(v).trim();
  const s = n.toFixed(8).replace(/\.?0+$/, "");
  return s === "" ? "0" : s;
}

function findTaxInCatalog(
  taxId: string,
  taxes: TaxEntity[] | undefined,
): TaxEntity | undefined {
  if (!taxes?.length || !taxId) return undefined;
  const id = String(taxId).trim();
  return taxes.find((t) => String(t.tax_id).trim() === id);
}

function displayLineTaxName(
  line: InvoiceBillLineLike,
  taxes: TaxEntity[] | undefined,
): string {
  const explicit = trimStr(line.tax_name);
  if (explicit.length > 0) return explicit;

  const tid = trimStr(line.tax_id);
  if (tid === "NO_TAX") return "No Tax";
  if (tid === "") return "";

  const hit = findTaxInCatalog(tid, taxes);
  if (hit?.tax_name?.trim()) return hit.tax_name.trim();
  return tid;
}

function displayLineTaxPercentage(
  line: InvoiceBillLineLike,
  taxes: TaxEntity[] | undefined,
): string {
  const tp = line.tax_percentage;
  if (typeof tp === "number" && !Number.isNaN(tp)) {
    return String(tp);
  }

  const tid = trimStr(line.tax_id);
  if (tid === "NO_TAX") return "0";
  if (tid === "") return "";

  const hit = findTaxInCatalog(tid, taxes);
  if (hit?.tax_percentage !== undefined && hit.tax_percentage !== null) {
    const p = parseFloat(String(hit.tax_percentage));
    return Number.isNaN(p) ? String(hit.tax_percentage) : String(p);
  }
  return "";
}

function formatTaxAmount(line: InvoiceBillLineLike): string {
  return formatNumberForDisplay(line.tax_amount);
}

function formatTdsName(line: InvoiceBillLineLike): string {
  return trimStr(line.tds_tax_name);
}

function formatTdsPercentage(line: InvoiceBillLineLike): string {
  const v = line.tds_tax_percentage;
  if (v === null || v === undefined || v === "") return "";
  const n = typeof v === "number" ? v : parseFloat(String(v));
  if (Number.isNaN(n)) return String(v);
  return String(n);
}

/**
 * Display string for one header field (allowlisted keys only).
 */
export function getDisplayValueForHeaderField(
  _kind: InvoiceOrBillKind,
  key: string,
  entity: InvoiceBillHeaderLike | null | undefined,
): string {
  if (!entity) return "";
  const v = entity[key];

  switch (key) {
    case "date":
    case "due_date":
      return formatIsoDateHeader(v);
    case "sub_total":
    case "tax_total":
    case "total":
    case "tcs_tax_percentage":
      return formatNumberForDisplay(v);
    case "exchange_rate":
      return formatExchangeRateHeaderDisplay(v);
    case "tcs_tax_name":
    case "customer_name":
    case "invoice_number":
    case "vendor_name":
    case "bill_number":
    case "currency_symbol":
      return v === null || v === undefined ? "" : String(v).trim();
    case "vendor_gstin":
    case "customer_gstin":
    case "gst_no":
      return normalizeBlankishGstinDisplay(v);
    default:
      return v === null || v === undefined ? "" : String(v).trim();
  }
}

/**
 * Display string for one line field (allowlisted keys only).
 */
export function getDisplayValueForLineField(
  kind: InvoiceOrBillKind,
  key: string,
  line: InvoiceBillLineLike | null | undefined,
  taxes?: TaxEntity[],
): string {
  if (!line) return "";

  if (key === "tax_name") {
    return displayLineTaxName(line, taxes);
  }
  if (key === "tax_percentage") {
    return displayLineTaxPercentage(line, taxes);
  }

  switch (key) {
    case "description":
    case "hsn_or_sac":
    case "account_name":
      return trimStr(line[key]);
    case "quantity":
    case "rate":
      return formatNumberForDisplay(line[key]);
    case "tax_amount":
      return formatTaxAmount(line);
    case "item_total":
    case "item_total_inclusive_of_tax":
      return formatNumberForDisplay(line[key]);
    case "tds_tax_name":
      if (kind !== "bill") return "";
      return formatTdsName(line);
    case "tds_tax_percentage":
      if (kind !== "bill") return "";
      return formatTdsPercentage(line);
    default:
      return "";
  }
}
